/* AMCR Meta click attribution v1.
 * Installation target: Shopify theme layout, after the Customer Privacy API is available.
 * Managed by the amcr-shopify-theme-install command.
 */
(function () {
  "use strict";

  const STORAGE_KEY = "amcr_meta_touch_v1";
  const SESSION_KEY = "amcr_meta_session_v1";
  const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
  const META_SOURCES = new Set([
    "facebook",
    "instagram",
    "threads",
    "messenger",
    "meta",
    "fb",
    "ig",
    "an",
    "msg",
  ]);
  const FIELDS = [
    "touch_at", "source", "medium", "campaign_name", "campaign_id",
    "adset_name", "adset_id", "ad_name", "ad_id", "landing_path",
    "referrer_domain", "fbclid_present", "fbclid_sha256", "captured_at",
    "consent_state", "consent_at", "session_id", "cart_token_sha256"
  ];

  function privacyApi() {
    return window.Shopify && window.Shopify.customerPrivacy;
  }

  function marketingAllowed() {
    const api = privacyApi();
    return Boolean(api && api.marketingAllowed());
  }

  function truncate(value, limit) {
    return String(value || "").slice(0, limit || 200);
  }

  function numericId(value) {
    const normalized = truncate(value, 32);
    return /^\d+$/.test(normalized) ? normalized : "";
  }

  async function sha256(value) {
    if (!value || !window.crypto || !window.crypto.subtle) return "";
    const bytes = new TextEncoder().encode(String(value));
    const digest = await window.crypto.subtle.digest("SHA-256", bytes);
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
  }

  function sessionId() {
    let value = window.sessionStorage.getItem(SESSION_KEY);
    if (!value) {
      value = window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      window.sessionStorage.setItem(SESSION_KEY, value);
    }
    return value;
  }

  function referrerDomain() {
    if (!document.referrer) return "";
    try {
      return truncate(new URL(document.referrer).hostname.toLowerCase(), 120);
    } catch (_) {
      return "";
    }
  }

  async function currentTouch() {
    const query = new URLSearchParams(window.location.search);
    const source = truncate(query.get("meta_source") || query.get("utm_source"), 80).toLowerCase();
    const campaignId = numericId(query.get("meta_campaign_id") || query.get("utm_id"));
    const adsetId = numericId(query.get("meta_adset_id"));
    const adId = numericId(query.get("meta_ad_id"));
    const fbclid = query.get("fbclid") || "";
    if (!META_SOURCES.has(source) && !campaignId && !adsetId && !adId && !fbclid) return null;

    const now = new Date().toISOString();
    return {
      touch_at: now,
      source,
      medium: truncate(query.get("utm_medium"), 80).toLowerCase(),
      campaign_name: truncate(query.get("utm_campaign")),
      campaign_id: campaignId,
      adset_name: truncate(query.get("utm_term")),
      adset_id: adsetId,
      ad_name: truncate(query.get("utm_content")),
      ad_id: adId,
      landing_path: truncate(window.location.pathname, 240),
      referrer_domain: referrerDomain(),
      fbclid_present: fbclid ? "1" : "0",
      fbclid_sha256: await sha256(fbclid),
      captured_at: now,
      consent_state: "marketing_allowed",
      consent_at: now,
      session_id: sessionId(),
      cart_token_sha256: ""
    };
  }

  function loadTouches() {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "null");
      if (!parsed || !parsed.first || !parsed.last) return null;
      const firstAt = Date.parse(parsed.first.touch_at);
      if (!Number.isFinite(firstAt) || Date.now() - firstAt > MAX_AGE_MS) return null;
      return parsed;
    } catch (_) {
      return null;
    }
  }

  function cartAttributes(touches) {
    const attributes = {};
    ["first", "last"].forEach((position) => {
      FIELDS.forEach((field) => {
        attributes[`__amcr_meta_${position}_${field}`] = truncate(touches[position][field], 240);
      });
    });
    attributes.__amcr_meta_schema = "v1";
    return attributes;
  }

  function clearedAttributes() {
    const attributes = {__amcr_meta_schema: ""};
    ["first", "last"].forEach((position) => {
      FIELDS.forEach((field) => { attributes[`__amcr_meta_${position}_${field}`] = ""; });
    });
    return attributes;
  }

  async function syncCart(touches) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    let cartTokenHash = "";
    try {
      const cartResponse = await fetch(`${root}cart.js`, {credentials: "same-origin"});
      if (cartResponse.ok) {
        const cart = await cartResponse.json();
        cartTokenHash = await sha256(cart.token || "");
      }
    } catch (_) {
      // The order attributes are still useful without a cart-token hash.
    }
    if (touches) {
      touches.first.cart_token_sha256 = cartTokenHash;
      touches.last.cart_token_sha256 = cartTokenHash;
    }
    await fetch(`${root}cart/update.js`, {
      method: "POST",
      credentials: "same-origin",
      headers: {"Content-Type": "application/json"},
      body: JSON.stringify({attributes: touches ? cartAttributes(touches) : clearedAttributes()})
    });
  }

  async function collect() {
    if (!marketingAllowed()) {
      window.localStorage.removeItem(STORAGE_KEY);
      window.sessionStorage.removeItem(SESSION_KEY);
      await syncCart(null).catch(function () {});
      return;
    }

    const touch = await currentTouch();
    let touches = loadTouches();
    if (touch) {
      touches = touches || {first: touch, last: touch};
      touches.last = touch;
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(touches));
    }
    if (touches) await syncCart(touches).catch(function () {});
  }

  function start() {
    if (start.started) return;
    start.started = true;
    collect();
    document.addEventListener("visitorConsentCollected", collect);
  }

  let loadRequested = false;
  function loadPrivacy() {
    if (privacyApi()) {
      start();
      return true;
    }
    if (!loadRequested && window.Shopify && typeof window.Shopify.loadFeatures === "function") {
      loadRequested = true;
      window.Shopify.loadFeatures(
        [{name: "consent-tracking-api", version: "0.1"}],
        function (error) {
          if (!error && privacyApi()) start();
        }
      );
      return true;
    }
    return false;
  }

  if (!loadPrivacy()) {
    let attempts = 0;
    const timer = window.setInterval(function () {
      attempts += 1;
      if (loadPrivacy() || attempts >= 40) {
        window.clearInterval(timer);
      }
    }, 250);
  }
})();
